import { useTranslations } from "next-intl";
import type { MockMessage } from "@/dummy";

type SystemPayload = NonNullable<MockMessage["system"]>;

/**
 * Pure selection of the localized string for a system notice. Kept separate
 * from the component so it can be unit-tested without a next-intl provider.
 */
export function systemNoticeText(
  system: SystemPayload,
  t: (key: string, values?: Record<string, string>) => string,
): string {
  if (system.type === "OPERATOR_CONNECTED") {
    return system.operatorName
      ? t("withOperator", { name: system.operatorName })
      : t("operatorConnected");
  }
  // CHAT_RESOLVED — attribute the resolution when the BE tells us who did it.
  if (system.initiator === "VISITOR") return t("chatResolvedByYou");
  if (system.initiator === "OPERATOR" && system.operatorName) {
    return t("chatResolvedByOperator", { name: system.operatorName });
  }
  return t("chatResolved");
}

/**
 * Centered lifecycle notice for an operator handoff (connected / resolved).
 * Mirrors the SYSTEM pill styling in `welcome-message.tsx` but is driven by a
 * runtime `system.type` rather than operator-configured welcome text.
 */
export function SystemNotice({
  system,
  messageId,
}: {
  system: SystemPayload;
  /** Same lookup the tab's scroll effect uses to find a newly-arrived row
   *  (see `data-message-id` on `MessageBubble`) — without it a connected/
   *  resolved notice that's the newest row never scrolls into view. */
  messageId?: string;
}) {
  const t = useTranslations("messages");
  const avatar = system.type === "OPERATOR_CONNECTED" ? system.operatorAvatar : undefined;
  return (
    <div className="flex items-center justify-center gap-1.5 px-2 py-1" data-message-id={messageId}>
      {avatar && (
        // biome-ignore lint/performance/noImgElement: operator photo of arbitrary host/dimensions; next/image needs known sizes + an allowlist
        <img src={avatar} alt="" className="h-4 w-4 shrink-0 rounded-full object-cover" />
      )}
      <span className="text-[11px] text-wx-fg-muted">{systemNoticeText(system, t)}</span>
    </div>
  );
}
